import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  color: white;
  overflow-x: hidden;
`;

const Header = styled.header`
  position: relative;
  padding: 2rem;
  text-align: center;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  text-decoration: none;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateX(-5px);
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
  background: linear-gradient(to right, #e94560, #ff6b6b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  max-width: 600px;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const PlatformBadges = styled(motion.div)`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const Badge = styled.a`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 50px;
  text-decoration: none;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  font-size: 0.95rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const MainContent = styled.div`
  background: #f9fafb;
  color: #333;
  padding: 5rem 2rem;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FeaturesSection = styled.section`
  margin-bottom: 5rem;
`;

const FeaturesTitle = styled.h2`
  font-size: 2.5rem;
  color: #e94560;
  text-align: center;
  margin-bottom: 3rem;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  }
`;

const FeatureIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1a1a2e, #e94560);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  color: #1a1a2e;
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
`;

const SpecSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e, #0f3460);
  color: white;
  padding: 4rem 2rem;
  border-radius: 24px;
  margin-bottom: 3rem;
`;

const SpecTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const SpecGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  max-width: 900px;
  margin: 0 auto;
`;

const SpecItem = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  backdrop-filter: blur(10px);
`;

const SpecLabel = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  opacity: 0.9;
`;

const SpecValue = styled.p`
  font-size: 1.3rem;
  font-weight: 600;
`;

const TokiPage: React.FC = () => {
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.6 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      <PageContainer>
        <Header>
          <BackButton to="/">
            ← Back to Portfolio
          </BackButton>

          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Toki
          </HeroTitle>

          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Track your time. Understand your day.
          </HeroSubtitle>

          <PlatformBadges
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <Badge href="https://ai4u.co.nz/toki" target="_blank" rel="noopener noreferrer">
              Web App
            </Badge>
            <Badge as="span" style={{ cursor: 'default', opacity: 0.7 }}>
              iOS (Coming Soon)
            </Badge>
          </PlatformBadges>
        </Header>

        <MainContent>
          <ContentContainer>
            <FeaturesSection>
              <FeaturesTitle>Key Features</FeaturesTitle>
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <FeaturesGrid>
                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>🕐</FeatureIcon>
                    <FeatureTitle>Custom Event Types</FeatureTitle>
                    <FeatureDescription>
                      Track sleep, commute, focus sessions, and any activity you define. Create your own event types to match your lifestyle.
                    </FeatureDescription>
                  </FeatureCard>

                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>😴</FeatureIcon>
                    <FeatureTitle>Sleep Statistics</FeatureTitle>
                    <FeatureDescription>
                      Analyze your sleep patterns with detailed statistics. Understand your sleep quality and trends over time.
                    </FeatureDescription>
                  </FeatureCard>

                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>📅</FeatureIcon>
                    <FeatureTitle>Scalable Calendar</FeatureTitle>
                    <FeatureDescription>
                      Visualize your tracked events on a zoomable calendar view. See your day, week, or month at a glance.
                    </FeatureDescription>
                  </FeatureCard>

                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>⏱️</FeatureIcon>
                    <FeatureTitle>Stopwatch</FeatureTitle>
                    <FeatureDescription>
                      Built-in timer to start and stop tracking activities in real time. One tap to begin, one tap to finish.
                    </FeatureDescription>
                  </FeatureCard>

                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>#️⃣</FeatureIcon>
                    <FeatureTitle>Hashtag Tagging</FeatureTitle>
                    <FeatureDescription>
                      Organize events with automatic hashtag labels. Quickly categorize and filter your tracked activities.
                    </FeatureDescription>
                  </FeatureCard>

                  <FeatureCard variants={cardVariants}>
                    <FeatureIcon>🔄</FeatureIcon>
                    <FeatureTitle>Cross-Platform Sync</FeatureTitle>
                    <FeatureDescription>
                      Real-time sync between iOS and Web via Firebase Firestore. Your data is always up to date across devices.
                    </FeatureDescription>
                  </FeatureCard>
                </FeaturesGrid>
              </motion.div>
            </FeaturesSection>

            <SpecSection>
              <SpecTitle>Technical Details</SpecTitle>
              <SpecGrid>
                <SpecItem>
                  <SpecLabel>iOS Framework</SpecLabel>
                  <SpecValue>SwiftUI</SpecValue>
                </SpecItem>
                <SpecItem>
                  <SpecLabel>Architecture</SpecLabel>
                  <SpecValue>MVVM</SpecValue>
                </SpecItem>
                <SpecItem>
                  <SpecLabel>Backend</SpecLabel>
                  <SpecValue>Firebase</SpecValue>
                </SpecItem>
                <SpecItem>
                  <SpecLabel>Web Stack</SpecLabel>
                  <SpecValue>React + Vite</SpecValue>
                </SpecItem>
                <SpecItem>
                  <SpecLabel>Extensions</SpecLabel>
                  <SpecValue>WidgetKit</SpecValue>
                </SpecItem>
                <SpecItem>
                  <SpecLabel>Shortcuts</SpecLabel>
                  <SpecValue>App Intents</SpecValue>
                </SpecItem>
              </SpecGrid>
            </SpecSection>
          </ContentContainer>
        </MainContent>
      </PageContainer>
    </motion.div>
  );
};

export default TokiPage;
